import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import ChatModerationBubble from "../containers/ChatModerationBubble";
import { QuoteProptypes } from "orm/resources/quote";
import Indicator from "coreUiKit/components/Chat/NewMessage/Indicator";
import CircularProgress from "coreUiKit/miscs/Progress";
import InfiniteScroll from "./InfiniteScrollListBeta";
import {
  EVENT_RESET_MODERATION_UNREAD_INDICATOR,
  EVENT_MODERATION_LIST_IS_SCROLLABLE,
  EVENT_CHAT_MODERATION_SCROLLDOWN_TRIGGER,
  EVENT_CHAT_MODERATION_DISPLAY_INDICATOR_NEW_MESSAGES,
} from "utilities/emitter/events";
import emitter from "utilities/emitter";

const useStyles = makeStyles(
  () => ({
    root: {
      width: "330px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      alignItems: "center",
      height: "100%",
      maxHeight: "100%",
      boxSizing: "border-box",
      position: "relative",
    },
    list: {
      flex: 1,
      boxSizing: "border-box",
      width: "100%",
      height: (props) => (props.height ? `${props.height}px` : "auto"),
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      paddingTop: "15px",
    },
    progress: {
      position: "absolute",
      top: 10,
      width: "100%",
      display: "flex",
      justifyContent: "center",
      flexDirection: "row",
    },
  }),
  {
    name: "ModerationList",
  }
);

const List = ({ Element, elements, total, fetchData }) => {
  const ref = useRef(null);

  const [atBottom, setAtBottom] = useState(false);
  const [height, setHeight] = useState(0);
  const classes = useStyles({ height });
  const [width, setWidth] = useState(315);
  const [hasNewQuotes, setHasNewQuotes] = useState(0);
  const [loading, showLoading] = useState(false);
  const [resize, setResize] = useState(0);

  const getListSize = () => {
    const newHeight = ref.current.clientHeight;
    setHeight(newHeight);

    const newWidth = ref.current.clientWidth;
    if (newWidth !== width) {
      setWidth(newWidth);
    }
  };
  // Get 'width' and 'height' after the initial render and every time the list changes
  useEffect(() => {
    getListSize();
  }, []);

  useEffect(() => {
    window.addEventListener("resize", getListSize);
    return () => {
      window.removeEventListener("resize", getListSize);
    };
  }, []);

  useEffect(() => {
    const onDisplayIndicator = ({ count }) => {
      setHasNewQuotes(count);
    };

    const resetHasNewQuotes = () => {
      setHasNewQuotes(0);
    };

    emitter.on(EVENT_CHAT_MODERATION_DISPLAY_INDICATOR_NEW_MESSAGES, onDisplayIndicator);
    emitter.on(EVENT_RESET_MODERATION_UNREAD_INDICATOR, resetHasNewQuotes);

    return () => {
      emitter.off(EVENT_CHAT_MODERATION_DISPLAY_INDICATOR_NEW_MESSAGES, onDisplayIndicator);
      emitter.off(EVENT_RESET_MODERATION_UNREAD_INDICATOR, resetHasNewQuotes);
    };
  }, []);
  const handleDisplayNewMessages = () => {
    emitter.emit(EVENT_CHAT_MODERATION_SCROLLDOWN_TRIGGER, {
      newQuotes: hasNewQuotes,
    });
  };

  const _fetchData = () => {
    showLoading(true);
    fetchData();
  };

  useEffect(() => {
    if (loading) {
      showLoading(false);
    }
  }, [elements.length]);
  const onScrollableList = (scrollable) => {
    emitter.emit(EVENT_MODERATION_LIST_IS_SCROLLABLE, scrollable);
  };
  if (!Element) return null;

  return (
    <Box className={classes.root}>
      {hasNewQuotes > 0 && !atBottom && <Indicator count={hasNewQuotes} onClick={handleDisplayNewMessages} />}
      {loading && (
        <Box className={classes.progress}>
          <CircularProgress />
        </Box>
      )}
      <Box id="chatListRoot" ref={ref} className={classes.list}>
        <InfiniteScroll
          fetchData={_fetchData}
          elements={elements}
          ItemComponent={Element}
          total={elements.length}
          height={height}
          width={width}
          reverse={false}
          variableSize={true}
          variableSizePadding={10}
          scrollDownEvent={EVENT_CHAT_MODERATION_SCROLLDOWN_TRIGGER}
          separatorEvent={EVENT_CHAT_MODERATION_DISPLAY_INDICATOR_NEW_MESSAGES}
          onScrollDownEvent={() => {
            emitter.emit(EVENT_RESET_MODERATION_UNREAD_INDICATOR);
          }}
          onReachBottom={(bottom) => {
            setAtBottom(bottom);
            if (bottom) {
              emitter.emit(EVENT_RESET_MODERATION_UNREAD_INDICATOR);
            }
          }}
          resize={resize}
          onScrollableList={onScrollableList}
        />
      </Box>
    </Box>
  );
};

List.propTypes = {
  Element: PropTypes.oneOf([ChatModerationBubble]),
  total: PropTypes.number,
  elements: PropTypes.arrayOf(QuoteProptypes),
  fetchData: PropTypes.func,
  reverse: PropTypes.bool,
  variableSize: PropTypes.bool,
};
List.defaultProps = {
  Element: ChatModerationBubble,
  elements: [],
  total: 0,
  fetchData: () => {},
};
export default List;
